import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './DNSInstructions.css';
import { buildApiUrl } from '../../config/api';

const DNSInstructions = () => {
  const { siteId } = useParams();
  const navigate = useNavigate();
  const [siteData, setSiteData] = useState(null);
  const [deploymentData, setDeploymentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedRecord, setCopiedRecord] = useState(null);

  useEffect(() => {
    fetchSiteData();
  }, [siteId]);

  const fetchSiteData = async () => {
    try {
      setLoading(true);
      
      // Fetch site details
      const siteResponse = await fetch(buildApiUrl(`api/sites/${siteId}`));
      const siteResult = await siteResponse.json();
      
      if (!siteResult.success) {
        throw new Error('Site not found');
      }
      
      setSiteData(siteResult.site);
      
      // Fetch deployment information
      const deploymentResponse = await fetch(buildApiUrl(`api/sites/${siteId}/deployments`));
      const deploymentResult = await deploymentResponse.json();
      
      if (deploymentResult.success) {
        setDeploymentData(deploymentResult.deployments);
      }
      
    } catch (err) {
      console.error('Error fetching site data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text, recordIndex) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedRecord(recordIndex);
      setTimeout(() => setCopiedRecord(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const generateDNSRecords = (customDomain, environment) => {
    if (!deploymentData) return [];
    
    const deployment = deploymentData[environment];
    if (!deployment || !deployment.isCustomDomain) return [];
    
    // Extract project name from Cloudflare URL
    const projectName = deployment.projectName;
    const cnameTarget = environment === 'production' 
      ? `${projectName}.pages.dev`
      : `preview.${projectName}.pages.dev`;
    
    // For external DNS providers like GoDaddy, we need to provide alternative instructions
    // since they don't support CNAME records for root domain (@)
    return [
      {
        type: 'CNAME',
        name: 'www',
        target: cnameTarget,
        description: 'Point www subdomain to Cloudflare Pages',
        note: 'This record works with all DNS providers'
      },
      {
        type: 'CNAME',
        name: '@',
        target: cnameTarget,
        description: 'Point root domain to Cloudflare Pages',
        note: 'This only works if your domain is managed by Cloudflare',
        warning: 'GoDaddy and most DNS providers do not support CNAME records for root domain (@)',
        alternative: 'For GoDaddy users: You need to either transfer your nameservers to Cloudflare or use a different setup'
      }
    ];
  };

  if (loading) {
    return (
      <div className="dns-instructions-page">
        <div className="dns-loading">
          <div className="loading-spinner">
            <i className="fas fa-spinner fa-spin"></i>
          </div>
          <h3>Loading DNS Instructions...</h3>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dns-instructions-page">
        <div className="dns-error">
          <div className="error-icon">
            <i className="fas fa-exclamation-triangle"></i>
          </div>
          <h3>Error Loading DNS Instructions</h3>
          <p>{error}</p>
          <button className="back-btn" onClick={() => navigate('/dashboard')}>
            <i className="fas fa-arrow-left"></i>
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Find custom domain deployments
  const productionDeployment = deploymentData?.production;
  const stagingDeployment = deploymentData?.staging;
  
  const hasCustomDomains = (productionDeployment?.isCustomDomain) || (stagingDeployment?.isCustomDomain);

  if (!hasCustomDomains) {
    return (
      <div className="dns-instructions-page">
        <div className="no-custom-domains">
          <div className="info-icon">
            <i className="fas fa-info-circle"></i>
          </div>
          <h3>No Custom Domains Configured</h3>
          <p>This site doesn't have any custom domains set up. Custom domains require DNS configuration.</p>
          <button className="back-btn" onClick={() => navigate('/dashboard')}>
            <i className="fas fa-arrow-left"></i>
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="dns-instructions-page">
      <div className="dns-header">
        <button className="back-btn" onClick={() => navigate('/dashboard')}>
          <i className="fas fa-arrow-left"></i>
          Back to Dashboard
        </button>
        
        <div className="header-content">
          <h1>
            <i className="fas fa-cog"></i>
            DNS Configuration
          </h1>
          <p>Configure your domain's DNS settings to point to your website</p>
        </div>
      </div>

      <div className="dns-content">
        <div className="site-info">
          <h2>{siteData.name}</h2>
          <p>Site ID: {siteData.siteId}</p>
        </div>

        {/* Production Custom Domain */}
        {productionDeployment?.isCustomDomain && (
          <div className="environment-section production">
            <div className="environment-header">
              <div className="env-badge production">
                <i className="fas fa-globe"></i>
                Production
              </div>
              <div className="domain-info">
                <h3>{productionDeployment.customDomain}</h3>
                <p>Live website for your visitors</p>
              </div>
            </div>

            <div className="dns-records-section">
              <h4>
                <i className="fas fa-list"></i>
                Required DNS Records
              </h4>
              
              <div className="dns-records">
                {generateDNSRecords(productionDeployment.customDomain, 'production').map((record, index) => (
                  <div key={index} className={`dns-record ${record.warning ? 'has-warning' : ''}`}>
                    <div className="record-info">
                      <div className="record-field">
                        <label>Type:</label>
                        <span className="record-value">{record.type}</span>
                      </div>
                      <div className="record-field">
                        <label>Name:</label>
                        <span className="record-value">{record.name}</span>
                      </div>
                      <div className="record-field">
                        <label>Points to:</label>
                        <span className="record-value">{record.target}</span>
                      </div>
                    </div>
                    
                    {/* Warning for incompatible records */}
                    {record.warning && (
                      <div className="record-warning">
                        <i className="fas fa-exclamation-triangle"></i>
                        <div className="warning-content">
                          <strong>Warning:</strong> {record.warning}
                          {record.alternative && (
                            <div className="alternative-text">
                              <strong>Alternative:</strong> {record.alternative}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Note for additional information */}
                    {record.note && !record.warning && (
                      <div className="record-note">
                        <i className="fas fa-info-circle"></i>
                        <span>{record.note}</span>
                      </div>
                    )}
                    
                    <button
                      className={`copy-record-btn ${copiedRecord === `prod-${index}` ? 'copied' : ''} ${record.warning ? 'disabled' : ''}`}
                      onClick={() => !record.warning && copyToClipboard(`${record.type} ${record.name} ${record.target}`, `prod-${index}`)}
                      title={record.warning ? 'This record is not compatible with your DNS provider' : 'Copy DNS Record'}
                      disabled={record.warning}
                    >
                      {record.warning ? (
                        <i className="fas fa-times"></i>
                      ) : copiedRecord === `prod-${index}` ? (
                        <i className="fas fa-check"></i>
                      ) : (
                        <i className="fas fa-copy"></i>
                      )}
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="current-status">
              <h4>
                <i className="fas fa-info-circle"></i>
                Current Status
              </h4>
              <div className="status-info">
                <div className="status-item">
                  <span className="status-label">Domain Status:</span>
                  <span className={`status-value ${productionDeployment.domainStatus || 'pending'}`}>
                    {productionDeployment.domainStatus || 'Pending DNS Configuration'}
                  </span>
                </div>
                <div className="status-item">
                  <span className="status-label">Cloudflare URL:</span>
                  <a href={productionDeployment.url} target="_blank" rel="noopener noreferrer" className="cloudflare-url">
                    {productionDeployment.url}
                  </a>
                </div>
                <div className="status-item">
                  <span className="status-label">Last Deployed:</span>
                  <span className="status-value">
                    {new Date(productionDeployment.lastDeployedAt).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Staging Custom Domain */}
        {stagingDeployment?.isCustomDomain && (
          <div className="environment-section staging">
            <div className="environment-header">
              <div className="env-badge staging">
                <i className="fas fa-flask"></i>
                Staging
              </div>
              <div className="domain-info">
                <h3>{stagingDeployment.customDomain}</h3>
                <p>Testing environment</p>
              </div>
            </div>

            <div className="dns-records-section">
              <h4>
                <i className="fas fa-list"></i>
                Required DNS Records
              </h4>
              
              <div className="dns-records">
                {generateDNSRecords(stagingDeployment.customDomain, 'staging').map((record, index) => (
                  <div key={index} className={`dns-record ${record.warning ? 'has-warning' : ''}`}>
                    <div className="record-info">
                      <div className="record-field">
                        <label>Type:</label>
                        <span className="record-value">{record.type}</span>
                      </div>
                      <div className="record-field">
                        <label>Name:</label>
                        <span className="record-value">{record.name}</span>
                      </div>
                      <div className="record-field">
                        <label>Points to:</label>
                        <span className="record-value">{record.target}</span>
                      </div>
                    </div>
                    
                    {/* Warning for incompatible records */}
                    {record.warning && (
                      <div className="record-warning">
                        <i className="fas fa-exclamation-triangle"></i>
                        <div className="warning-content">
                          <strong>Warning:</strong> {record.warning}
                          {record.alternative && (
                            <div className="alternative-text">
                              <strong>Alternative:</strong> {record.alternative}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Note for additional information */}
                    {record.note && !record.warning && (
                      <div className="record-note">
                        <i className="fas fa-info-circle"></i>
                        <span>{record.note}</span>
                      </div>
                    )}
                    
                    <button
                      className={`copy-record-btn ${copiedRecord === `staging-${index}` ? 'copied' : ''} ${record.warning ? 'disabled' : ''}`}
                      onClick={() => !record.warning && copyToClipboard(`${record.type} ${record.name} ${record.target}`, `staging-${index}`)}
                      title={record.warning ? 'This record is not compatible with your DNS provider' : 'Copy DNS Record'}
                      disabled={record.warning}
                    >
                      {record.warning ? (
                        <i className="fas fa-times"></i>
                      ) : copiedRecord === `staging-${index}` ? (
                        <i className="fas fa-check"></i>
                      ) : (
                        <i className="fas fa-copy"></i>
                      )}
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="current-status">
              <h4>
                <i className="fas fa-info-circle"></i>
                Current Status
              </h4>
              <div className="status-info">
                <div className="status-item">
                  <span className="status-label">Domain Status:</span>
                  <span className={`status-value ${stagingDeployment.domainStatus || 'pending'}`}>
                    {stagingDeployment.domainStatus || 'Pending DNS Configuration'}
                  </span>
                </div>
                <div className="status-item">
                  <span className="status-label">Cloudflare URL:</span>
                  <a href={stagingDeployment.url} target="_blank" rel="noopener noreferrer" className="cloudflare-url">
                    {stagingDeployment.url}
                  </a>
                </div>
                <div className="status-item">
                  <span className="status-label">Last Deployed:</span>
                  <span className="status-value">
                    {new Date(stagingDeployment.lastDeployedAt).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* GoDaddy Specific Instructions */}
        <div className="godaddy-instructions">
          <h3>
            <i className="fas fa-exclamation-triangle"></i>
            Special Instructions for GoDaddy Users
          </h3>
          
          <div className="godaddy-warning">
            <div className="warning-header">
              <i className="fas fa-info-circle"></i>
              <h4>Important: GoDaddy Limitation</h4>
            </div>
            <p>
              GoDaddy and most DNS providers <strong>do not support CNAME records for the root domain (@)</strong>. 
              This is a DNS standard limitation, not a Cloudflare issue.
            </p>
          </div>

          <div className="godaddy-solutions">
            <h4>
              <i className="fas fa-lightbulb"></i>
              Solutions for GoDaddy Users
            </h4>
            
            <div className="solution-options">
              <div className="solution-option recommended">
                <div className="solution-header">
                  <i className="fas fa-star"></i>
                  <h5>Option 1: Transfer Nameservers to Cloudflare (Recommended)</h5>
                </div>
                <div className="solution-content">
                  <p><strong>Benefits:</strong> Full DNS control, CNAME flattening support, better performance</p>
                  <div className="solution-steps">
                    <div className="step">
                      <span className="step-number">1</span>
                      <span>Add your domain to Cloudflare (free account)</span>
                    </div>
                    <div className="step">
                      <span className="step-number">2</span>
                      <span>Change nameservers in GoDaddy to Cloudflare's nameservers</span>
                    </div>
                    <div className="step">
                      <span className="step-number">3</span>
                      <span>Add CNAME records in Cloudflare (works for root domain)</span>
                    </div>
                  </div>
                  <a 
                    href="https://developers.cloudflare.com/dns/zone-setups/full-setup/setup/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="solution-link"
                  >
                    <i className="fas fa-external-link-alt"></i>
                    View Cloudflare Setup Guide
                  </a>
                </div>
              </div>

              <div className="solution-option">
                <div className="solution-header">
                  <i className="fas fa-globe"></i>
                  <h5>Option 2: Use www Subdomain Only</h5>
                </div>
                <div className="solution-content">
                  <p><strong>Limitation:</strong> Your root domain won't work, only www.yourdomain.com</p>
                  <div className="solution-steps">
                    <div className="step">
                      <span className="step-number">1</span>
                      <span>Add only the www CNAME record (this works in GoDaddy)</span>
                    </div>
                    <div className="step">
                      <span className="step-number">2</span>
                      <span>Set up a redirect from root domain to www in GoDaddy</span>
                    </div>
                  </div>
                  <div className="godaddy-record-example">
                    <h6>GoDaddy DNS Record:</h6>
                    <div className="record-example">
                      <div className="record-field">
                        <label>Type:</label>
                        <span>CNAME</span>
                      </div>
                      <div className="record-field">
                        <label>Name:</label>
                        <span>www</span>
                      </div>
                      <div className="record-field">
                        <label>Value:</label>
                        <span>{productionDeployment?.projectName || 'your-project'}.pages.dev</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="solution-option">
                <div className="solution-header">
                  <i className="fas fa-exchange-alt"></i>
                  <h5>Option 3: Switch to a Different DNS Provider</h5>
                </div>
                <div className="solution-content">
                  <p><strong>Alternative:</strong> Use a DNS provider that supports CNAME flattening</p>
                  <div className="provider-alternatives">
                    <div className="provider-alt">
                      <strong>Cloudflare DNS:</strong> Free, supports CNAME flattening
                    </div>
                    <div className="provider-alt">
                      <strong>Route 53:</strong> AWS service, supports ALIAS records
                    </div>
                    <div className="provider-alt">
                      <strong>DNSimple:</strong> Supports ALIAS records
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="godaddy-help">
            <div className="help-note">
              <i className="fas fa-question-circle"></i>
              <div>
                <strong>Need help with GoDaddy setup?</strong>
                <p>
                  The "Record data is invalid" error you're seeing is because GoDaddy doesn't allow CNAME records 
                  for the root domain (@). This is normal and expected.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Setup Guide */}
        <div className="dns-setup-guide">
          <h3>
            <i className="fas fa-list-ol"></i>
            Step-by-Step Setup Instructions
          </h3>
          
          <div className="setup-steps">
            <div className="setup-step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h4>Access Your Domain Provider</h4>
                <p>Log in to your domain registrar's control panel (GoDaddy, Namecheap, Cloudflare, etc.)</p>
              </div>
            </div>
            
            <div className="setup-step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h4>Navigate to DNS Settings</h4>
                <p>Look for "DNS Management", "DNS Records", or "Advanced DNS" in your control panel</p>
              </div>
            </div>
            
            <div className="setup-step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h4>Add DNS Records</h4>
                <p>Add the CNAME records shown above. Delete any existing A records for @ and www if present.</p>
              </div>
            </div>
            
            <div className="setup-step">
              <div className="step-number">4</div>
              <div className="step-content">
                <h4>Save Changes</h4>
                <p>Save your DNS settings and wait for propagation (up to 24 hours)</p>
              </div>
            </div>
          </div>
        </div>

        {/* Provider Guides */}
        <div className="provider-guides">
          <h3>
            <i className="fas fa-question-circle"></i>
            Common Domain Providers
          </h3>
          
          <div className="provider-tabs">
            <div className="provider-guide">
              <h4><i className="fas fa-external-link-alt"></i> GoDaddy</h4>
              <p>Go to "My Products" → "DNS" → "Manage Zones" → Add CNAME records</p>
            </div>
            
            <div className="provider-guide">
              <h4><i className="fas fa-external-link-alt"></i> Namecheap</h4>
              <p>Go to "Domain List" → "Manage" → "Advanced DNS" → Add CNAME records</p>
            </div>
            
            <div className="provider-guide">
              <h4><i className="fas fa-external-link-alt"></i> Cloudflare</h4>
              <p>Go to "DNS" → "Records" → Add CNAME records (turn off proxy for initial setup)</p>
            </div>
          </div>
        </div>

        {/* Troubleshooting */}
        <div className="dns-troubleshooting">
          <h3>
            <i className="fas fa-tools"></i>
            Troubleshooting
          </h3>
          
          <div className="troubleshooting-items">
            <div className="troubleshooting-item">
              <i className="fas fa-exclamation-triangle"></i>
              <div>
                <strong>Domain not working after 24 hours?</strong>
                <p>Check that you've added both @ and www CNAME records correctly</p>
              </div>
            </div>
            
            <div className="troubleshooting-item">
              <i className="fas fa-search"></i>
              <div>
                <strong>Check DNS propagation:</strong>
                <p>Use tools like <a href="https://whatsmydns.net" target="_blank" rel="noopener noreferrer">whatsmydns.net</a> to check if your DNS changes have propagated</p>
              </div>
            </div>
            
            <div className="troubleshooting-item">
              <i className="fas fa-times-circle"></i>
              <div>
                <strong>Remove conflicting records:</strong>
                <p>Delete any existing A records for @ and www before adding CNAME records</p>
              </div>
            </div>
          </div>
        </div>

        {/* Help Information */}
        <div className="dns-help">
          <div className="help-item">
            <i className="fas fa-info-circle"></i>
            <span>DNS changes can take up to 24 hours to propagate worldwide.</span>
          </div>
          <div className="help-item">
            <i className="fas fa-clock"></i>
            <span>Your site will be accessible via the Cloudflare URL immediately.</span>
          </div>
          <div className="help-item">
            <i className="fas fa-shield-alt"></i>
            <span>SSL certificate will be automatically provisioned once DNS is configured.</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DNSInstructions; 